import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Multiselect from 'multiselect-react-dropdown'
import { ArrowLeft, Save } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/Checkbox'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Alert } from '@/components/ui/alert'
import { useAuth } from '@/contexts/AuthContext'
import { useUser } from '@/hooks/useUsers'
import { usersService, UpdateUserPayload } from '@/services/usersService'
import { useNotifications } from '@/contexts/NotificationContext'
import { paths } from '@/routes/path'
import { UserRole } from '@/models/user.model'
import { departmentsService } from '@/services/departmentsService'
import { Department } from '@/models/department.model'

type FormValues = {
  firstName: string
  lastName: string
  email: string
  phone: string
  level: string
  stage: string
  programmeId: string
  programmeCohortId: string
  programmeCohortAssignedAt: string
  programmeCohortCompletedAt: string
  departmentIds: string[]
  isActive: boolean
}

function toDateInputValue(timestamp: string | null): string {
  if (!timestamp) return ''
  try {
    const date = new Date(timestamp)
    return date.toISOString().substring(0, 10)
  } catch {
    return ''
  }
}

export function StudentEditPage() {
  const { user: currentUser, isLoading: authLoading } = useAuth()
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { addNotification } = useNotifications()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const {
    data: learner,
    isLoading,
    isError,
    error,
  } = useUser(userId)

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      level: '',
      stage: '',
      programmeId: '',
      programmeCohortId: '',
      programmeCohortAssignedAt: '',
      programmeCohortCompletedAt: '',
      departmentIds: [],
      isActive: true,
    },
  })

  const {
    data: departments,
    isLoading: departmentsLoading,
  } = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => departmentsService.getDepartments(),
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (!learner) return

    reset({
      firstName: learner.firstName ?? '',
      lastName: learner.lastName ?? '',
      email: learner.email ?? '',
      phone: learner.phone ?? '',
      level: learner.level ? String(learner.level) : '',
      stage: learner.stage ?? '',
      programmeId: learner.programmeId ?? '',
      programmeCohortId: learner.programmeCohortId ?? '',
      programmeCohortAssignedAt: toDateInputValue(learner.programmeCohortAssignedAt ?? null),
      programmeCohortCompletedAt: toDateInputValue(learner.programmeCohortCompletedAt ?? null),
      departmentIds: learner.departmentId ? [learner.departmentId] : [],
      isActive: Boolean(learner.isActive),
    })
  }, [learner, reset])

  const updateLearnerMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      setErrorMessage(null)
      if (!userId) throw new Error('Missing learner identifier')

      const payload: UpdateUserPayload = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        isActive: values.isActive,
      }

      if (values.phone.trim()) {
        payload.phone = values.phone.trim()
      }

      if (values.level) {
        const parsedLevel = Number(values.level)
        if (!Number.isNaN(parsedLevel)) {
          payload.level = parsedLevel
        }
      }

      if (values.stage.trim()) {
        payload.stage = values.stage.trim()
      }

      if (values.programmeId.trim()) {
        payload.programmeId = values.programmeId.trim()
      }

      if (values.programmeCohortId.trim()) {
        payload.programmeCohortId = values.programmeCohortId.trim()
      }

      if (values.programmeCohortAssignedAt) {
        payload.programmeCohortAssignedAt = values.programmeCohortAssignedAt
      }

      if (values.programmeCohortCompletedAt) {
        payload.programmeCohortCompletedAt = values.programmeCohortCompletedAt
      }

      if (values.departmentIds.length) {
        payload.departmentIds = values.departmentIds
      }

      return usersService.updateUser(userId, payload)
    },
    onSuccess: (updatedLearner) => {
      setErrorMessage(null)
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
      if (updatedLearner.id) {
        queryClient.invalidateQueries({ queryKey: ['users', 'detail', updatedLearner.id] })
      }

      addNotification({
        title: 'Learner updated',
        message: `${updatedLearner.fullName || 'Learner'} has been updated successfully.`,
        type: 'general',
        priority: 'low',
        isRead: false,
        isArchived: false,
      })

      navigate(updatedLearner.id ? paths.app.users.detail.build(updatedLearner.id) : paths.app.studentManagement.root.absolute)
    },
    onError: (mutationError: unknown) => {
      const message = mutationError instanceof Error ? mutationError.message : 'Unable to update learner'
      setErrorMessage(message)
      addNotification({
        title: 'Update failed',
        message,
        type: 'general',
        priority: 'medium',
        isRead: false,
        isArchived: false,
      })
    },
  })

  const onSubmit = handleSubmit(async (values) => {
    await updateLearnerMutation.mutateAsync(values)
  })

  const isAdmin = currentUser?.role === UserRole.ADMIN

  const completionSummary = useMemo(() => {
    if (!learner) return 'Not assigned to cohort'
    if (learner.programmeCohortCompletedAt) return 'Completed cohort'
    if (learner.programmeCohortAssignedAt) return 'Currently enrolled cohort'
    return 'Not assigned to cohort'
  }, [learner?.programmeCohortAssignedAt, learner?.programmeCohortCompletedAt, learner])

  if (authLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="max-w-md border-secondary-200">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-secondary-900">Access restricted</CardTitle>
            <CardDescription className="text-secondary-600">
              Learner information can only be edited by administrators.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (isError || !learner) {
    const message = error instanceof Error ? error.message : 'We could not load this learner.'
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <p className="text-lg font-semibold text-secondary-900">Learner not available</p>
        <p className="mt-1 max-w-md text-sm text-secondary-600">{message}</p>
        <Link to={paths.app.studentManagement.root.absolute}>
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to students
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <Button variant="outline" type="button" onClick={() => navigate(-1)} className="text-secondary-600">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="flex items-center gap-3">
          {learner.id && (
            <Link to={paths.app.users.detail.build(learner.id)}>
              <Button variant="ghost" type="button" className="text-secondary-600 hover:text-secondary-900">
                View profile
              </Button>
            </Link>
          )}
          <Button
            type="submit"
            disabled={!isDirty || isSubmitting || updateLearnerMutation.isPending}
            className="flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-70"
          >
            <Save className="h-4 w-4" />
            Save changes
          </Button>
        </div>
      </div>

      <Card className="border border-secondary-100 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-secondary-900">Update learner information</CardTitle>
          <CardDescription className="text-secondary-600">
            Maintain accurate enrolment details while keeping the learner active status under control.
          </CardDescription>
          {errorMessage && (
            <Alert variant="destructive" className="mt-4">
              {errorMessage}
            </Alert>
          )}
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="firstName">
                First name *
              </label>
              <Input
                id="firstName"
                {...register('firstName', { required: 'First name is required' })}
              />
              {errors.firstName && <p className="text-xs text-red-500">{errors.firstName.message}</p>}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="lastName">
                Last name *
              </label>
              <Input
                id="lastName"
                {...register('lastName', { required: 'Last name is required' })}
              />
              {errors.lastName && <p className="text-xs text-red-500">{errors.lastName.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="email">
                Email
              </label>
              <Input id="email" disabled {...register('email')} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="phone">
                Phone number
              </label>
              <Input id="phone" {...register('phone')} placeholder="+234 800 000 0000" />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="level">
                Level
              </label>
              <Input id="level" type="number" min={0} {...register('level')} placeholder="e.g. 200" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="stage">
                Stage
              </label>
              <Input id="stage" {...register('stage')} placeholder="e.g. Stage 2" />
            </div>
          </div>

          {/* <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="programmeId">
                Programme ID
              </label>
              <Input id="programmeId" {...register('programmeId')} placeholder="Programme identifier" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="programmeCohortId">
                Programme Cohort ID
              </label>
              <Input id="programmeCohortId" {...register('programmeCohortId')} placeholder="Cohort identifier" />
            </div>
          </div> */}

          {/* <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="programmeCohortAssignedAt">
                Cohort Assigned At
              </label>
              <Input id="programmeCohortAssignedAt" type="date" {...register('programmeCohortAssignedAt')} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="programmeCohortCompletedAt">
                Cohort Completed At
              </label>
              <Input id="programmeCohortCompletedAt" type="date" {...register('programmeCohortCompletedAt')} />
            </div>
          </div> */}

          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">
              Departments
            </label>
            <Controller
              name="departmentIds"
              control={control}
              render={({ field }) => {
                const departmentOptions =
                  departments?.map((dept: Department) => ({
                    id: dept.id,
                    label: dept.name,
                  })) ?? []

                const selected = departmentOptions.filter((option) =>
                  (field.value ?? []).includes(option.id),
                )

                return (
                  <div className="rounded-lg border border-secondary-200 bg-white p-2">
                    <Multiselect
                      options={departmentOptions}
                      selectedValues={selected}
                      displayValue="label"
                      placeholder={departmentsLoading ? 'Loading departments...' : 'Select departments'}
                      onSelect={(selectedList) => field.onChange(selectedList.map((item: any) => item.id))}
                      onRemove={(selectedList) => field.onChange(selectedList.map((item: any) => item.id))}
                      showCheckbox
                      avoidHighlightFirstOption
                      disable={!departments?.length}
                      hidePlaceholder={false}
                      style={{
                        chips: {
                          background: '#7b2a40',
                          borderRadius: '12px',
                        },
                        searchBox: {
                          border: 'none',
                        },
                      }}
                    />
                    {!departmentsLoading && !departments?.length && (
                      <p className="mt-2 text-xs text-secondary-500">
                        No departments available. Please create a department first.
                      </p>
                    )}
                  </div>
                )
              }}
            />
          </div>

          <div className="rounded-lg border border-secondary-100 bg-secondary-50 px-4 py-3">
            <Checkbox label="Active learner" {...register('isActive')} />
            <p className="mt-1 text-xs text-secondary-500">
              Deactivate a learner to temporarily restrict access without removing their records.
            </p>
            <p className="mt-2 text-xs font-medium text-secondary-600">
              Cohort status: {completionSummary}
            </p>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
